import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import c from 'classnames';
import { AccessLevel } from 'lavva.exalushome/build/js/Services/Users/IUser';
import Stack from '@mui/material/Stack';
import { IconChevron, IconNotification } from '../../assets/icons';
import { useInstallationContext } from '../../context/installation';
import { IntegrationType } from '../../data-access/gql-types/graphql';
import StateSyncButton from '../../integrations/exalus/components/state-sync-button';
import { useLoggedUser } from '../../integrations/exalus/hooks/common/logged-user';
import { ROUTES } from '../../routes/routes';
import { useNotificationsStore } from '../../store/notifications';
import { InstallationIconTypeEnum } from '../../types';
import { isLocalApp } from '../../utils/helpers/local-app';
import { InstallationIcon } from '../edit-installation-icon';
import { PopUpNav, PopUpNavLink } from '../popup-nav';
import './main-header.scss';

type MainHeaderProps = {
  navLinks?: PopUpNavLink[];
};

export const MainHeader: React.FC<MainHeaderProps> = ({ navLinks }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const { user } = useLoggedUser();
  const countOfUnreadNotifications = useNotificationsStore((s) => s.countOfUnreadNotifications);
  const { selectedInstallation, installationsLoading, currentInstallationLoading, integrationType } =
    useInstallationContext();
  const isExalus = integrationType === IntegrationType.Exalus;
  const loading = !!(currentInstallationLoading || installationsLoading);

  const goToNotificationslist = () => navigate(ROUTES.Notification());

  const handleRedirectToInstallationList = () => navigate(ROUTES.Installation());

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      className="main__header"
      sx={{ margin: '0 0 24px 0' }}
    >
      <div
        className={c('main__header--installation', { exalus: isExalus })}
        onClick={handleRedirectToInstallationList}
      >
        <InstallationIcon
          iconName={selectedInstallation?.iconName || InstallationIconTypeEnum.INSTALLATION_0}
          activeColor={selectedInstallation?.hexColor}
          size={36}
          loading={loading}
        />
        {!loading && (
          <>
            <span className="main__header--installation-name">
              {selectedInstallation ? selectedInstallation.name : t('noSelectedInstallation')}
            </span>
            <IconChevron direction="right" noSpace size={22} />
          </>
        )}
      </div>
      <Stack direction="row" alignItems="center">
        {!isLocalApp && (
          <button className="main__header--notification" onClick={goToNotificationslist}>
            <IconNotification isOn={!!countOfUnreadNotifications} />
          </button>
        )}
        {isExalus && <StateSyncButton />}
        {selectedInstallation && user?.AccessLevel !== AccessLevel.Guest && navLinks && (
          <PopUpNav className="main__header--popup-nav" links={navLinks} />
        )}
      </Stack>
    </Stack>
  );
};
